// Mappers for UserManagementService — kept out of the service file so it
// only has to read as "which endpoint, which payload", not also carry the
// response/request shape normalization logic.

// The form works with role names (e.g. "Admin"), but the backend expects the
// numeric roleId on create/edit — this is the one fixed mapping between the
// two. roleIdToNameMap is derived from it (not hand-written separately) so
// the two directions can never drift out of sync with each other.
const roleNameToIdMap: Record<string, number> = {
  "Standard User": 0,
  Client: 1,
  AdminOwner: 2,
  Admin: 3,
};

const roleIdToNameMap: Record<number, string> = Object.fromEntries(
  Object.entries(roleNameToIdMap).map(([name, id]) => [id, name]),
);

export const getRoleIdByName = (roleName: string): number | null =>
  roleNameToIdMap[roleName] ?? null;

export const getRoleNameById = (roleId: number): string | null =>
  roleIdToNameMap[roleId] ?? null;

export interface UserDetails {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string; // role name, matches roleOptions' value on the Edit User form
  client: string[]; // ids of clients already assigned to this user
}

// The form (and roleOptions' value) always works with the role name string —
// this resolves one no matter which shape the backend sent the role in:
// already a name somewhere, or only a numeric roleId that needs the reverse
// lookup above.
const resolveRoleName = (raw: any): string => {
  const name =
    raw?.role?.roleName ??
    raw?.role?.name ??
    raw?.roleName ??
    (typeof raw?.role === "string" ? raw.role : undefined);
  if (name) return name;

  const id =
    raw?.role?.roleId ??
    raw?.roleId ??
    (typeof raw?.role === "number" ? raw.role : undefined);
  return id !== undefined ? (getRoleNameById(id) ?? "") : "";
};

// Raw field names aren't confirmed yet — tolerant of common variants, same
// approach as rbac.mapper.ts, until checked against a real response.
export const mapUserDetails = (raw: any): UserDetails => ({
  userId: raw?.userId ?? raw?.UserId ?? raw?.id ?? raw?.Id ?? "",
  firstName: raw?.firstName ?? raw?.FirstName ?? "",
  lastName: raw?.lastName ?? raw?.LastName ?? "",
  email: raw?.email ?? raw?.Email ?? "",
  role: resolveRoleName(raw),
  client: raw?.clientIds ?? raw?.clients ?? raw?.assignedClients ?? [],
});

export interface ClientSearchResult {
  id: string;
  name: string;
}

// Raw business client field names aren't confirmed yet — tolerant of the
// common variants until checked against a real response, same approach as
// rbac.mapper.ts.
export const mapBusinessClient = (client: any): ClientSearchResult => ({
  id:
    client?.id ??
    client?.Id ??
    client?.businessClientId ??
    client?.clientId ??
    "",
  name:
    client?.name ??
    client?.Name ??
    client?.businessName ??
    client?.clientName ??
    client?.companyName ??
    "",
});
